import { createContext, useContext, useState, useCallback } from 'react';
import authFetch from '../utils/authFetch';

// The one shared conversation. Mounted once at the layout level
// (CustomLayout.js), above the router, so it survives route navigation and
// is never recreated by either the compact widget or the full /assistant
// page — both read/call through this same provider instance.
const AssistantContext = createContext(null);

export const AssistantProvider = ({ children }) => {
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', content, proposal?}[]
  const [sending, setSending]   = useState(false);
  const [view, setView]         = useState('closed'); // 'closed' | 'compact'

  // Per-proposal resolution state (pending/confirming/confirmed/declined/…),
  // keyed by the proposal's own token. Lives here rather than in local
  // useState on ProposalCard so it survives that component being unmounted
  // and remounted — e.g. closing/reopening the compact panel, or switching
  // between compact and the full /assistant page — the same principle
  // `messages` above already follows.
  const [proposalStates, setProposalStates] = useState({}); // token -> { state, secondsLeft, errorMessage }

  const setProposalState = useCallback((token, updates) => {
    setProposalStates(prev => {
      const prevEntry = prev[token] || {};
      const patch = typeof updates === 'function' ? updates(prevEntry) : updates;
      return { ...prev, [token]: { ...prevEntry, ...patch } };
    });
  }, []);

  // Stateless backend — the full conversation is resent on every turn, so
  // `messages` here IS the source of truth for what /assistant sees next,
  // not just a rendering of it. Moved verbatim from the original
  // AssistantPage.js (pre-widget revision).
  // Returns true/false so callers (e.g. AssistantConversation's input box)
  // can react to a failed send, such as restoring the typed text.
  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending) return false;

    const nextMessages = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setSending(true);

    try {
      const res = await authFetch('/assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Resend the full conversation every turn — only role/content go
        // over the wire, never the proposal cards' own local state.
        body: JSON.stringify({ messages: nextMessages.map(({ role, content }) => ({ role, content })) }),
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        setMessages(ms => [...ms, { role: 'assistant', content: body.error || 'אירעה שגיאה בפנייה לעוזר החכם' }]);
        return false;
      }
      const reply = typeof body.reply === 'string' ? body.reply : 'אירעה שגיאה בפנייה לעוזר החכם';
      setMessages(ms => [...ms, { role: 'assistant', content: reply, proposal: body.proposal || undefined }]);
      return true;
    } catch (e) {
      setMessages(ms => [...ms, { role: 'assistant', content: 'שגיאת תקשורת — נסה שוב' }]);
      return false;
    } finally {
      setSending(false);
    }
  }, [messages, sending]);

  const value = { messages, sending, view, setView, sendMessage, proposalStates, setProposalState };

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
};

export const useAssistant = () => {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used within an AssistantProvider');
  return ctx;
};
